import { getAuditLogs, queryKeys } from '@vgrubs/components';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

const PAGE_SIZE = 20;

/** Root margin (px) below viewport - fetch next page when sentinel is this far from bottom */
const PRELOAD_ROOT_MARGIN = '1000px';

export type AuditLogFilters = {
  query: string;
  from: string;
  to: string;
};

const fetchAuditLogsPage = async ({
  pageParam = 1,
  query,
  from,
  to,
}: {
  pageParam: number;
  query: string;
  from: string;
  to: string;
}) => {
  const { data } = await getAuditLogs({
    query,
    from,
    to,
    page: pageParam,
    limit: PAGE_SIZE,
  });

  const items = data?.data ?? [];
  const nextPage = items.length === PAGE_SIZE ? pageParam + 1 : undefined;

  return {
    logs: items,
    nextPage,
  };
};

export const useAuditLogInfiniteScroll = (filters: AuditLogFilters) => {
  const { query, from, to } = filters;
  const observerRef = useRef<HTMLDivElement | null>(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status,
    isFetching,
  } = useInfiniteQuery({
    queryKey: queryKeys.admin.auditLogs.list(query, from, to),
    queryFn: ({ pageParam }) =>
      fetchAuditLogsPage({ pageParam, query, from, to }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
  });

  useEffect(() => {
    const el = observerRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry?.isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage &&
          !isFetching
        ) {
          fetchNextPage();
        }
      },
      {
        threshold: 0,
        rootMargin: `0px 0px ${PRELOAD_ROOT_MARGIN} 0px`,
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, fetchNextPage, isFetchingNextPage, isFetching]);

  const logs = data?.pages.flatMap((page) => page.logs) ?? [];

  return {
    logs,
    observerRef,
    isFetchingNextPage,
    isFetching,
    status,
    hasNextPage,
  };
};
